import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { trackCtaClicked, trackMenuClicked } from '../../utils/analytics';
import { Hero } from './Hero';
import { WhatIs } from './WhatIs';
import { Features } from './Features';
import { InstallApps } from './InstallApps';
import { WorkflowSteps } from './WorkflowSteps';
import { Pricing } from './Pricing';
import { Community } from './Community';
import { FAQ } from './FAQ';
import { EarlyAccess } from './EarlyAccess';
import { FormSection } from './FormSection';
import { Footer } from './Footer';
import { GoToMyDeskLabel } from './GoToMyDeskLabel';
import { useLocale } from '../../contexts/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';

interface Section {
  id: string;
  label: string | null;
  isLogo?: boolean;
}

export const Landing: React.FC = () => {
  const { t } = useLocale();
  const { token } = useAuth();

  const logoUrl = useMemo(
    () =>
      new URL(
        '../../assets/logos/SVG/POSITIVO/logo scribane_positivo completo h adjusted.svg',
        import.meta.url,
      ).toString(),
    [],
  );

  const navLogoUrl = useMemo(
    () =>
      new URL(
        '../../assets/logos/SVG/NEGATIVO/logo scribane_negativo micro simple adjusted.svg',
        import.meta.url,
      ).toString(),
    [],
  );

  const sections = useMemo(
    () => [
      { id: 'hero', label: null, isLogo: true },
      { id: 'features', label: t('landing_nav_features') },
      { id: 'pricing', label: t('landing_nav_pricing') },
      { id: 'chapter-zero', label: t('landing_nav_early_access') },
      { id: 'faq', label: t('landing_nav_faq') },
    ],
    [t],
  );

  const [activeSectionId, setActiveSectionId] = useState<string>('hero');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .s-reveal {
        transform: translateY(28px);
        transition: transform 540ms cubic-bezier(0.22,1,0.36,1);
      }
      .s-reveal.s-visible {
        transform: none;
      }
      @media (prefers-reduced-motion: reduce) {
        .s-reveal {
          transform: none;
          transition: none;
        }
      }
    `;
    document.head.appendChild(style);

    const ids = ['what-is', 'features', 'also-included', 'apps', 'workflow', 'for-you', 'pricing', 'chapter-zero', 'faq', 'cta', 'footer', 'early-access'];
    const els = ids.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[];

    requestAnimationFrame(() => {
      els.forEach(el => el.classList.add('s-reveal'));
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('s-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const observedElements = sections
      .map((section) => document.getElementById(section.id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (observedElements.length === 0) {
      return;
    }

    const getRootMargin = (): string => {
      const viewportHeight = window.innerHeight;
      if (viewportHeight < 640) {
        return '-10% 0px -40% 0px';
      } else if (viewportHeight < 1024) {
        return '-15% 0px -55% 0px';
      }
      return '-20% 0px -70% 0px';
    };

    let observer: IntersectionObserver | null = null;

    const setupObserver = (): void => {
      if (observer) {
        observer.disconnect();
      }

      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0));

          const topVisible = visible[0];
          const id = topVisible?.target?.id;
          if (id) {
            setActiveSectionId(id);
          }
        },
        {
          root: null,
          rootMargin: getRootMargin(),
          threshold: [0.05, 0.1, 0.2, 0.35, 0.5, 0.75],
        },
      );

      for (const element of observedElements) {
        observer.observe(element);
      }
    };

    setupObserver();

    const handleScroll = (): void => {
      if (window.scrollY < 100) {
        setActiveSectionId('hero');
        return;
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollBottom = scrollTop + windowHeight;

      if (scrollBottom >= documentHeight - 50) {
        setActiveSectionId('footer');
        return;
      }

      const scrollPosition = window.scrollY + window.innerHeight * 0.3;
      let currentSection = 'hero';
      let minDistance = Infinity;

      for (const element of observedElements) {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + window.scrollY;
        const elementBottom = elementTop + rect.height;
        const distance = Math.abs(scrollPosition - elementTop);

        if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
          if (distance < minDistance) {
            minDistance = distance;
            currentSection = element.id;
          }
        }
      }

      if (minDistance !== Infinity) {
        setActiveSectionId(currentSection);
      }
    };

    const handleResize = (): void => {
      setupObserver();
      handleScroll();
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [sections]);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-2 min-[864px]:gap-6 px-8 min-[864px]:px-8">
          {/* Logo — always visible */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); scrollToSection('hero'); setMenuOpen(false); }}
            className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring flex-shrink-0"
            aria-label={t('go_to_top')}
          >
            <img src={logoUrl} alt="Scribane" className="h-8 min-[864px]:h-12 w-auto" />
          </a>

          {/* Desktop nav */}
          <nav className="hidden min-[864px]:flex flex-1 items-center justify-end gap-3 min-w-0 overflow-hidden">
            <ul className="flex items-center gap-1 overflow-x-auto rounded-full border border-gray-200 bg-white/60 p-1 text-sm text-gray-700 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {sections.map((section) => {
                const isActive = activeSectionId === section.id;
                if (section.isLogo) {
                  return (
                    <li key={section.id} className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => { trackMenuClicked(section.id); scrollToSection(section.id); }}
                        className={[
                          'rounded-full p-2 transition-colors flex items-center justify-center',
                          isActive ? 'bg-[var(--scribane-black)]' : 'hover:bg-gray-100',
                        ].join(' ')}
                        aria-current={isActive ? 'page' : undefined}
                        aria-label={t('go_to_top')}
                      >
                        <img
                          src={navLogoUrl}
                          alt="Scribane"
                          className={[
                            'h-4 w-4 transition-opacity',
                            isActive ? 'opacity-100' : 'opacity-60',
                          ].join(' ')}
                        />
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={section.id} className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => { trackMenuClicked(section.id); scrollToSection(section.id); }}
                      className={[
                        'rounded-full px-3 py-2 transition-colors whitespace-nowrap text-sm',
                        isActive ? 'bg-[var(--scribane-black)] text-white' : 'hover:bg-gray-100',
                      ].join(' ')}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {section.label}
                    </button>
                  </li>
                );
              })}
            </ul>
            {token ? (
              <Button
                asChild
                variant="inverse"
                className="flex-shrink-0"
              >
                <Link to="/desk" className="inline-flex items-center gap-2">
                  <GoToMyDeskLabel />
                </Link>
              </Button>
            ) : (
              <Button
                asChild
                variant="inverse"
                className="flex-shrink-0"
              >
                <Link to="/login">
                  {t('landing_cta_sign_in')}
                </Link>
              </Button>
            )}
            {!token && (
              <Button
                onClick={() => { trackCtaClicked(t('landing_cta_start'), 'nav'); scrollToSection('cta'); }}
                className="flex-shrink-0 rounded-[10px] py-[11px] px-5 h-auto text-[14px] bg-[var(--scribane-green)] text-[var(--scribane-black)] hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)] transition-[background,color] duration-[220ms]"
              >
                {t('landing_cta_start')}
              </Button>
            )}
          </nav>

          {/* Mobile right: CTA + hamburger */}
          <div className="flex min-[864px]:hidden items-center gap-2">
            {token ? (
              <Button
                asChild
                className="rounded-[10px] py-[9px] px-4 h-auto text-[13px] bg-[var(--scribane-green)] text-[var(--scribane-black)] hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)] transition-[background,color] duration-[220ms]"
              >
                <Link to="/desk" className="inline-flex items-center gap-2">
                  <GoToMyDeskLabel />
                </Link>
              </Button>
            ) : (
              <Button
                onClick={() => { trackCtaClicked(t('landing_cta_start'), 'nav_mobile'); scrollToSection('cta'); setMenuOpen(false); }}
                className="rounded-[10px] py-[9px] px-4 h-auto text-[13px] bg-[var(--scribane-green)] text-[var(--scribane-black)] hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)] transition-[background,color] duration-[220ms]"
              >
                {t('landing_cta_start')}
              </Button>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="p-2 rounded-md text-[var(--scribane-black)] hover:bg-gray-100 transition-colors"
              aria-expanded={menuOpen}
              aria-label="Menú"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="min-[864px]:hidden border-t border-gray-200 bg-white/95 backdrop-blur px-8 py-4 flex flex-col gap-1">
            {sections.filter((s) => !s.isLogo).map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => { trackMenuClicked(section.id); scrollToSection(section.id); setMenuOpen(false); }}
                className="text-left px-3 py-3 rounded-md text-[15px] text-[var(--scribane-black)] hover:bg-gray-100 transition-colors"
              >
                {section.label}
              </button>
            ))}
            {token ? (
              <Button asChild variant="inverse" className="mt-2 w-full" onClick={() => setMenuOpen(false)}>
                <Link to="/desk" className="inline-flex items-center gap-2">
                  <GoToMyDeskLabel />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="inverse" className="mt-2 w-full" onClick={() => setMenuOpen(false)}>
                <Link to="/login">{t('landing_cta_sign_in')}</Link>
              </Button>
            )}
          </div>
        )}
      </header>

      <Hero />
      <WhatIs />
      <Features logoUrl={logoUrl} />
      <InstallApps />
      <WorkflowSteps />
      <EarlyAccess />
      <Pricing />
      <Community />
      <FAQ />
      <FormSection />
      <Footer />
    </div>
  );
};
